import dayjs from "dayjs";
import { object, string } from "yup";

import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import ICustomer from "@/interfaces/customer.interfaces";
import ISalesProductPrice from "@/interfaces/sales-product-price.interfaces";
import ISalesProduct, { SalesProductStatus } from "@/interfaces/sales-product.interfaces";
import { ISubscribeInput } from "@/interfaces/subscribe.interfaces";
import { SubscribeFormMessages } from "@/messages/subscribe/subscribe-form.messages";
import { CustomerSalesProductStatus } from "@/types/customer-sales-product.types";
import { ToastProps } from "@/types/toast.types";

import { SubscribeFormData } from "./types";

export const buildFormInitialData = (
  customer: ICustomer | null,
  salesProduct: ISalesProduct | null,
  salesProductPrice: ISalesProductPrice | null
): SubscribeFormData => {
  return {
    name: customer?.name || "",
    document: customer?.federalTaxId || "",
    zipCode: customer?.zipCode || "",
    address: customer?.address || "",
    number: customer?.addressNumber || "",
    complement: customer?.addressComp || "",
    neighborhood: customer?.district || "",
    city: customer?.city || "",
    state: customer?.state || "",

    salesProduct: {
      id: salesProduct?.id || 0,
      code: salesProduct?.code || "",
      name: salesProduct?.name || "",
      description: salesProduct?.description || "",
      status: salesProduct?.status || SalesProductStatus.Inactive,
      productImage: salesProduct?.productImage || "",
      rowDate: salesProduct?.rowDate || new Date(),
    },

    salesProductPrice: {
      planName: salesProductPrice?.planName || "",
      startDate: salesProductPrice?.startDate || new Date(),
      endDate: salesProductPrice?.endDate || null,
      pricePerEvent: salesProductPrice?.pricePerEvent || 0,
      eventsIncludedMonthlyFee: salesProductPrice?.eventsIncludedMonthlyFee || 0,
      feePerSalesConvertion: salesProductPrice?.feePerSalesConvertion || 0,
      fixedFeePerSalesConvertion: salesProductPrice?.fixedFeePerSalesConvertion || 0,
      fixedMonthlyFee: salesProductPrice?.fixedMonthlyFee || 0,
      planDescription: salesProductPrice?.planDescription || "",
      planContrast: salesProductPrice?.planContrast || false,
      id: salesProductPrice?.id || 0,
      rowDate: salesProductPrice?.rowDate || new Date(),
      salesProductId: salesProductPrice?.salesProductId || 0,
    },
  };
};

export const buildFormSchema = (messages: SubscribeFormMessages) => {
  return object().shape({
    name: string().required(messages.errorRequiredName),
    document: string().required(messages.errorRequiredCnpjCpf),
    zipCode: string().required(messages.errorRequiredCep),
    address: string().required(messages.errorRequiredAddress),
    number: string().required(messages.errorRequiredNumber),
    neighborhood: string().required(messages.errorRequiredNeighborhood),
    city: string().required(messages.errorRequiredCity),
    state: string().required(messages.errorRequiredState),
  });
};

export const normalizeSubscribeData = (
  formData: SubscribeFormData,
  customer: ICustomer
): ISubscribeInput => {
  const { salesProduct, salesProductPrice } = formData;

  return {
    salesProductId: salesProduct.id,
    startDate: new Date().toISOString(),
    endDate: dayjs().add(1, "month").toDate().toISOString(),
    salesProductPriceId: salesProductPrice.id,
    planName: salesProductPrice.planName,
    pricePerEvent: salesProductPrice.pricePerEvent,
    eventsIncludedMonthlyFee: salesProductPrice.eventsIncludedMonthlyFee,
    feePerSalesConvertion: salesProductPrice.feePerSalesConvertion,
    fixedFeePerSalesConvertion: salesProductPrice.fixedFeePerSalesConvertion,
    fixedMonthlyFee: salesProductPrice.fixedMonthlyFee,
    nextPriceUpdate: new Date().toISOString(),
    dueDay: 1,
    customer: {
      ...customer,
      name: formData.name,
      federalTaxId: formData.document,
      address: formData.address,
      addressNumber: formData.number,
      addressComp: formData.complement || "",
      district: formData.neighborhood,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
    },
  };
};

export const hasExistingPlan = (
  salesProductPrice: ISalesProductPrice,
  showToast: (toast: ToastProps) => void,
  existingPlanError: string,
  customerSalesProducts?: ICustomerSalesProduct[]
): void => {
  if (!customerSalesProducts) return;

  const hasExistingPlan = customerSalesProducts.some(
    (product) =>
      product.salesProduct.id === salesProductPrice.salesProductId &&
      product.status === CustomerSalesProductStatus.Active
  );

  if (hasExistingPlan) return showToast({ text: existingPlanError, type: "error" });
};
